import { FC, useEffect, useRef, useState } from 'react'
import notificationSound from '../../assets/sounds/notification.wav'
import LastLookModal from '../../components/Modal/LastLookModal/LastLookModal'
import { useOpenFin } from '../../helpers/hooks/useOpenFin'
import { getUserOrOperatorOrderById } from '../../store/order/selectors'
import { Order } from '../../store/order/types'
import { SecurityStaticData } from '../../store/securities/reducer'
import { getSecurityStaticDataById } from '../../store/securities/selectors'
import { useAppDispatch, useAppSelector } from '../../store/types'
import { getUserPreferences } from '../../store/userPreferences/selectors'
import { removeLastLookFromWindows } from '../../store/windows/actions'
import { getLastLookWindows } from '../../store/windows/selectors'
import { LastLookWindowInfo } from '../../store/windows/types'
import Popout from '../Popouts/Popout'
import { isOpenFinRuntime, MAIN_WINDOW_NAME } from './helpers'

const lastLookAudio = new Audio(notificationSound)

const playLastLookSound = () => {
  lastLookAudio.currentTime = 0
  lastLookAudio.play().catch((err) => {
    console.error('LastLook notification audio failed', err)
  })
}

interface LastLookRemovedMessage {
  type: 'removed'
  windowInfo: LastLookWindowInfo
}

const buildNotificationBody = (
  order: Order | undefined,
  security: SecurityStaticData | undefined
): string => {
  if (!order?.aggressorOrder) return 'New order needs your approval'
  const type = order.type === 'buy' ? 'Buy' : 'Sell'
  const size = order.aggressorOrder.size
  return security?.boardLabel
    ? `${type} order of ${size} for ${security.boardLabel} needs your approval`
    : `${type} order of ${size} needs your approval`
}

const fireNotification = (orderId: string, body: string): void => {
  const notification = new Notification('Last Look Order', {
    body,
    tag: `lastLook-${orderId}`,
    requireInteraction: true
  })
  notification.onclick = () => {
    window.focus()
    notification.close()
  }
}

const requestNotificationPermission = (): void => {
  if (!('Notification' in window)) return
  if (Notification.permission === 'default') {
    void Notification.requestPermission()
  }
}

const showLastLookNotification = (orderId: string, body: string): void => {
  if (!('Notification' in window)) return
  if (document.visibilityState === 'visible') return
  if (Notification.permission !== 'granted') return
  fireNotification(orderId, body)
}

const LastLookAudio: FC<{ muteNotifications: boolean }> = ({
  muteNotifications
}) => {
  useEffect(() => {
    if (muteNotifications) return
    playLastLookSound()
  }, [muteNotifications])
  return null
}

const LastLookCoordinator: FC<{
  windowInfo: LastLookWindowInfo
  muteNotifications: boolean
}> = ({ windowInfo, muteNotifications }) => {
  const [hasLock, setHasLock] = useState(false)

  useEffect(() => {
    if (!('locks' in navigator)) {
      setHasLock(true)
      return
    }
    let release: (() => void) | null = null
    let unmounted = false
    navigator.locks
      .request(
        `lastLook:${windowInfo.orderId}`,
        { ifAvailable: true },
        (lock) => {
          if (!lock || unmounted) {
            return
          }
          setHasLock(true)
          return new Promise<void>((resolve) => {
            release = resolve
          })
        }
      )
      .catch((err) => {
        console.error('LastLook lock request failed', err)
      })
    return () => {
      unmounted = true
      release?.()
    }
  }, [windowInfo.orderId])

  useEffect(() => {
    if (!hasLock || muteNotifications) return
    playLastLookSound()
  }, [hasLock, muteNotifications])

  if (!hasLock) return null
  return (
    <Popout title={`LastLook/${windowInfo.orderId}`} windowInfo={windowInfo}>
      <LastLookModal windowInfo={windowInfo} />
    </Popout>
  )
}

const LastLookOpener: FC = () => {
  const dispatch = useAppDispatch()
  const { openPlatformWindow } = useOpenFin()
  const lastLookWindows = useAppSelector(getLastLookWindows)
  const { muteNotifications } = useAppSelector(getUserPreferences)
  const getOrder = useAppSelector(getUserOrOperatorOrderById)
  const getSecurity = useAppSelector(getSecurityStaticDataById)

  const notifiedOrderIds = useRef(new Set<string>())
  useEffect(() => {
    const currentIds = new Set(lastLookWindows.map((w) => w.orderId))
    let hasNew = false
    lastLookWindows.forEach((windowInfo) => {
      if (!notifiedOrderIds.current.has(windowInfo.orderId)) {
        hasNew = true
        notifiedOrderIds.current.add(windowInfo.orderId)
        const order = getOrder(windowInfo.orderId)
        const security = order ? getSecurity(order.securityId) : undefined
        showLastLookNotification(
          windowInfo.orderId,
          buildNotificationBody(order, security)
        )
      }
    })
    if (hasNew) requestNotificationPermission()
    notifiedOrderIds.current.forEach((id) => {
      if (!currentIds.has(id)) notifiedOrderIds.current.delete(id)
    })
  }, [lastLookWindows, getOrder, getSecurity])

  useEffect(() => {
    if (!isOpenFinRuntime()) return
    if (fin.me.identity.name !== MAIN_WINDOW_NAME) return
    lastLookWindows.forEach((windowInfo) => {
      void openPlatformWindow(`LastLook/${windowInfo.orderId}`)
    })
  }, [lastLookWindows, openPlatformWindow])

  useEffect(() => {
    if (isOpenFinRuntime() || !('BroadcastChannel' in window)) return
    const channel = new BroadcastChannel('last-look-events')
    channel.onmessage = (e: MessageEvent<LastLookRemovedMessage>) => {
      if (e.data.type === 'removed') {
        dispatch(removeLastLookFromWindows(e.data.windowInfo))
      }
    }
    return () => channel.close()
  }, [dispatch])

  if (lastLookWindows.length === 0) return null
  if (isOpenFinRuntime()) {
    if (fin.me.identity.name !== MAIN_WINDOW_NAME) return null
    return (
      <>
        {lastLookWindows.map((windowInfo) => (
          <LastLookAudio
            key={windowInfo.orderId}
            muteNotifications={muteNotifications}
          />
        ))}
      </>
    )
  }
  return (
    <>
      {lastLookWindows.map((windowInfo) => (
        <LastLookCoordinator
          key={windowInfo.orderId}
          windowInfo={windowInfo}
          muteNotifications={muteNotifications}
        />
      ))}
    </>
  )
}

export default LastLookOpener
